import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'

import { IContentFeed } from '../queries/getContentFeeds'
import {
  IUpsertContentFeedResponse,
  IUpsertContentFeedVariables,
  upsertContentFeedMutation
} from '../mutations/upsertContentFeed'
import {
  ISetContentFeedTopicsResponse,
  ISetContentFeedTopicsVariables,
  setContentFeedTopicsMutation
} from '../mutations/setContentFeedTopics'

export interface IFeedSettings {
  name: string
  description: string
  enabled: boolean
  topicIds: string[]
}

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every(id => b.includes(id))

/**
 * A feed's settings held as a draft until published.
 *
 * Shared by the feed detail page and the featured band on Content home, which
 * both edit the same four fields through the same modal. Keeping one copy
 * matters: the trailing-whitespace deadlock below is subtle enough that two
 * implementations would drift.
 */
const useFeedSettings = (feed: IContentFeed | null) => {
  const [upsertContentFeed] = useMutation<
    IUpsertContentFeedResponse,
    IUpsertContentFeedVariables
  >(upsertContentFeedMutation)
  const [setContentFeedTopics] = useMutation<
    ISetContentFeedTopicsResponse,
    ISetContentFeedTopicsVariables
  >(setContentFeedTopicsMutation)

  const saved: IFeedSettings = useMemo(
    () => ({
      name: feed?.name ?? '',
      description: feed?.description ?? '',
      enabled: feed?.enabled ?? false,
      topicIds: feed?.topics.map(topic => topic.id) ?? []
    }),
    [feed]
  )

  const [settings, setSettings] = useState<IFeedSettings>(saved)

  // Adopt server values only while the form is untouched; otherwise a cache
  // update would silently discard a pending draft.
  const touched = useRef(false)
  useEffect(() => {
    if (touched.current) return
    setSettings(saved)
  }, [saved])

  /**
   * What a save would actually send. Compare THIS against `saved`, never the raw
   * form values.
   *
   * The form keeps exactly what was typed, but the server trims the name
   * (Yup .trim() on upsertContentFeed) and we trim the description on the way
   * out. Comparing raw input against the stored value makes a trailing space a
   * change that can never be published away: we send the trimmed name, the
   * server stores what it already had, `saved` never moves, the resync effect
   * bails because the form is touched, and the publish bar reports a pending
   * change forever.
   */
  const outgoing = {
    name: settings.name.trim(),
    description: settings.description.trim()
  }

  const changeCount =
    (outgoing.name !== saved.name ? 1 : 0) +
    (outgoing.description !== saved.description ? 1 : 0) +
    (settings.enabled !== saved.enabled ? 1 : 0) +
    (sameIds(settings.topicIds, saved.topicIds) ? 0 : 1)

  const update = (patch: Partial<IFeedSettings>) => {
    touched.current = true
    setSettings(current => ({ ...current, ...patch }))
  }

  const reset = () => {
    touched.current = false
    setSettings(saved)
  }

  /**
   * Writes only the parts that changed. Throws on failure so the caller can keep
   * the draft and surface the error rather than losing the edits.
   */
  const publish = async () => {
    if (!feed || !changeCount) return

    // Two mutations because topics live in their own join table. Fields go
    // first: if topics fail, the name change is still saved and the retry is
    // just the topic write.
    if (
      outgoing.name !== saved.name ||
      outgoing.description !== saved.description ||
      settings.enabled !== saved.enabled
    ) {
      await upsertContentFeed({
        variables: {
          id: feed.id,
          name: outgoing.name,
          description: outgoing.description || null,
          enabled: settings.enabled
        }
      })
    }
    if (!sameIds(settings.topicIds, saved.topicIds)) {
      await setContentFeedTopics({
        variables: { contentFeedId: feed.id, topicIds: settings.topicIds }
      })
    }

    // Show what was actually stored. Without this the field keeps whitespace the
    // server discarded, which reads as an unsaved edit.
    setSettings(current => ({ ...current, ...outgoing }))
  }

  return { settings, saved, changeCount, update, reset, publish }
}

export default useFeedSettings
